import { Flex, Table, Text } from "@mantine/core";
import { IconDeviceIpadOff } from "@tabler/icons-react";
import {
  type CSSProperties,
  Fragment,
  type HTMLAttributes,
  memo,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { useIsOnline } from "src/hooks";
import { StyledTr } from "src/styled";

import Loader from "../Loader/Loader";
import {
  ScrollTableBody,
  ScrollTableHead,
  ScrollTablePageWrapper,
  ScrollTableWrapper,
} from "./ScrollableTable.styled";
import ScrollableTableHeaderCell from "./ScrollableTableHeaderCell";
import ScrollableTableRow, {
  type TRenderScrollableRow,
} from "./ScrollableTableRow";

export interface ITableHeader extends HTMLAttributes<HTMLTableCellElement> {
  name?: string;
  label: string;
  minWidth?: number;
  sortable?: boolean;
}

interface Props<T> {
  data: T[] | undefined;
  headers: ITableHeader[];
  renderRow: TRenderScrollableRow<T>;
  keyExtractor?: (row: T, index: string) => string;
  loading?: boolean;
  style?: CSSProperties;
  sortable?: boolean;
  divider?: boolean;
  red?: boolean;
  isPdfExport?: boolean;
  pageStickyHeaders?: boolean;
  stickyHeaderTop?: number | string;
}

const ScrollableTable = <T extends object>({
  data,
  headers,
  renderRow,
  loading,
  keyExtractor,
  style,
  sortable,
  divider,
  red,
  isPdfExport,
  pageStickyHeaders,
  stickyHeaderTop,
}: Props<T>) => {
  const isOnline = useIsOnline();
  // Deliberately not useSearchParams(): that hook subscribes to the router's
  // location context, forcing every table on the page to re-render on any
  // navigation (e.g. Advanced Filters Apply), even when nothing here changed.
  const navigate = useNavigate();
  useEffect(() => {
    if (!isOnline) {
      const params = new URLSearchParams(window.location.search);
      if (params.has("sort") || params.has("dir")) {
        params.delete("sort");
        params.delete("dir");
        navigate(`${window.location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    }
  }, [isOnline, navigate]);

  const table = (
    <Table
      style={{
        borderCollapse: "collapse",
        width: "100%",
      }}>
      <ScrollTableHead pageSticky={pageStickyHeaders}>
        <StyledTr>
          {headers.map(cell => (
            <ScrollableTableHeaderCell
              key={cell.name ?? cell.label}
              style={{ minWidth: cell.minWidth ? cell.minWidth : "initial" }}
              sortable={sortable && isOnline && cell.sortable !== false}
              red={red}
              {...cell}
            />
          ))}
        </StyledTr>
      </ScrollTableHead>

      {!loading && data && data.length > 0 && (
        <ScrollTableBody>
          {data?.map((row, index) => {
            const rowKey = keyExtractor
              ? keyExtractor(row, String(index))
              : String(index);
            return (
              <Fragment key={rowKey}>
                {divider && index > 0 && <StyledTr style={{ height: 50 }} />}
                <ScrollableTableRow
                  row={row}
                  render={renderRow}
                  index={index}
                />
              </Fragment>
            );
          })}
        </ScrollTableBody>
      )}
    </Table>
  );

  const emptyState = !loading && (!data || data.length === 0) && (
    <Flex direction="column" align="center" justify="center" gap={8} mt={32}>
      <IconDeviceIpadOff size={60} />
      <Text fz={20} ta="center">
        There is no data to display
      </Text>
    </Flex>
  );

  const wrapperStyle: CSSProperties = {
    marginTop: 12,
    ...(stickyHeaderTop !== undefined
      ? {
          ["--scroll-table-sticky-top" as string]:
            typeof stickyHeaderTop === "number"
              ? `${stickyHeaderTop}px`
              : stickyHeaderTop,
        }
      : {}),
    ...style,
  };

  if (isPdfExport) {
    return (
      <div style={{ marginTop: 12, width: "100%", ...style }}>{table}</div>
    );
  }

  if (pageStickyHeaders) {
    return (
      <ScrollTablePageWrapper style={wrapperStyle}>
        {table}
        {loading && <Loader color={red ? "redText" : "blue"} />}
        {emptyState}
      </ScrollTablePageWrapper>
    );
  }

  return (
    <ScrollTableWrapper
      type="auto"
      scrollbarSize={12}
      offsetScrollbars="present"
      style={wrapperStyle}>
      {table}
      {loading && <Loader color={red ? "redText" : "blue"} />}
      {emptyState}
    </ScrollTableWrapper>
  );
};

export default memo(ScrollableTable) as typeof ScrollableTable;
